import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { ApiError, makeApiFetch, type ApiFetch } from "./base";

export function useApi(): ApiFetch {
  const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } =
    useAuth0();

  const on401 = useCallback(() => {
    void loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
    });
  }, [loginWithRedirect]);

  const getToken = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      return await getAccessTokenSilently();
    } catch {
      return null;
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  return useMemo(() => makeApiFetch(getToken, on401), [getToken, on401]);
}

export type AsyncState<T> = {
  data: T | null;
  isLoading: boolean;
  error: Error | null;
  reload: () => void;
};

// Lightweight async hook — no react-query dep, mirrors RN's manual useEffect+fetch pattern.
export function useApiResource<T>(
  fn: (apiFetch: ApiFetch) => Promise<T>,
  deps: ReadonlyArray<unknown>
): AsyncState<T> {
  const apiFetch = useApi();
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  // Use a ref so deps changes are the only retrigger; fn identity isn't.
  const fnRef = useRef(fn);
  fnRef.current = fn;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fnRef
      .current(apiFetch)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          // Silenced 401s — useApi's on401 already redirects.
          if (!(err instanceof ApiError && err.status === 401)) {
            setError(err instanceof Error ? err : new Error(String(err)));
          }
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiFetch, tick, ...deps]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, isLoading, error, reload };
}
